import React, { createContext, useState, useEffect } from 'react';
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  from,
  split,
  gql,
} from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import * as ActionCable from '@rails/actioncable';
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink';

import secrets from '../../secrets';
import storage from '../utils/storage';
import { sendError } from '../components/ErrorBoundary';

const APP_SHORT_NAME = secrets.app.name.split('-')[1];
const VERSION_KEY = 'X-Equiplinc-Version';
const VERSION = process.env.REACT_APP_VERSION;
const USER_KEY = 'X-User-Id';
const REMEMBER_ME_KEY = 'remember-me';

const CURRENT_USER = gql`
  query {
    currentUser {
      id
      email
      firstName
      lastName
      fullName
      role
      canAccessCommissions
      canApproveBuyer
      canApproveSeller
      defaultNotificationsEmail
      liveSaleNotificationsEmail
      availableForChat
      accounting
      admin
      policies {
        model
        action
      }
    }
    accounts {
      value: id
      label: name
      id
      name
      special
      accountGroupDescription
      type
      posting
      hiddenAt
    }
    feeTypes {
      value: name
      label: name
      id
      name
      shortName
      markup
    }
    paddleServices {
      id
      name
      code
      presaleScreen
    }
    paymentTerms {
      value: id
      label: name
      id
      name
      days
    }
    lotPrefixes {
      code
    }
    settings {
      storageFeeText
      feeMarkups {
        id
        feeType
        markupType
        amount
        defaultMarkup
      }
    }
  }
`;

export const AuthContext = createContext();

const cable = ActionCable.createConsumer(secrets.api.baseUri + '/cable');
const actionCableLink = new ActionCableLink({ cable });

const setupApolloClient = options => {
  const { clearCredentials, user, checkVersion, ...uploadOptions } = options;

  const uploadLink = createUploadLink(uploadOptions);

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription';
    },
    actionCableLink,
    uploadLink,
  );

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (networkError) {
      if (401 === networkError.statusCode) {
        clearCredentials();
      }
    } else if (graphQLErrors && graphQLErrors[0]) {
      if ('NOT_FOUND' === graphQLErrors[0].extensions?.code) {
        window.location.href = '/';
      } else if (graphQLErrors[0].extensions?.code !== 'USER_ERROR') {
        sendError({
          error: {
            name: 'GraphQL Error',
            message: graphQLErrors[0].message,
            stack: JSON.stringify(graphQLErrors),
          },
          user
        });
      }
    }
  });

  const checkHeadersLink = new ApolloLink((operation, forward) => {
    return forward(operation).map(data => {
      const context = operation.getContext();

      if (context.response?.headers) {
        localStorage.setItem(
          'X-User-Log-Id',
          context.response.headers.get('X-User-Log-Id')
        );

        checkVersion(context.response.headers.get(VERSION_KEY));
      }

      return data;
    });
  });

  return new ApolloClient({
    link: from([checkHeadersLink, errorLink, splitLink]),
    cache: new InMemoryCache({
      typePolicies: {
        UserNotification: {
          fields: {
            notification: { merge: true }
          }
        }
      }
    }),
  });
};

export const AuthProvider = ({ children }) => {
  const [credentials, setCredentials] = useState();
  const [data, setData] = useState();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [storageChecked, setStorageChecked] = useState(false);
  const [userId, setUserId] = useState();
  const [rememberMe, setRememberMe] = useState();
  const [loginAttempted, setLoginAttempted] = useState(false);
  const [newVersion, setNewVersion] = useState(false);
  const [ignoreVersion, setIgnoreVersion] = useState(true);

  const updateCredentials = values => {
    storage.save({ key: 'credentials', data: values }).then(() => {
      if (credentials?.accessToken !== values?.accessToken) {
        setCredentials(values);
      }
    });
  };

  const updateUserIdWithoutLog = id => {
    storage.save({ key: USER_KEY, data: { id } }).then(() => {
      if (userId !== id) {
        setUserId(id);
      }
    });
  };

  const updateRememberMe = email => {
    storage.save({ key: REMEMBER_ME_KEY, data: { email } }).then(() => {
      if (rememberMe !== email) {
        setRememberMe(email);
      }
    });
  };

  const clearCredentials = () => {
    updateUserIdWithoutLog(null);
    updateCredentials(null);
    setData(null);
    setCredentials(null);
  };

  const updateFromStorage = () => {
    storage.load({ key: 'credentials' }).then(values => {
      setCredentials(values);
      setStorageChecked(true);

      if (values?.accessToken) {
        storage.load({ key: USER_KEY }).then(values => {
          setUserId(values?.id);
        });
      }
    });

    storage.load({ key: REMEMBER_ME_KEY }).then(values => {
      setRememberMe(values?.email);
    });
  };

  const checkVersion = versionsFromAPI => {
    if (!VERSION) { return; }
    if (!versionsFromAPI) { return; }

    if (ignoreVersion && (
      new Date().getTime() > ignoreVersion + (60 * 60 * 1000)
    )) {
      setIgnoreVersion(false);
    }

    let versionFromAPI;

    versionsFromAPI.split(';').forEach(value => {
      if (APP_SHORT_NAME === value.split(':')[0]) {
        versionFromAPI = value.split(':')[1]
      }
    });

    if (!versionFromAPI) { return; }

    if (parseInt(versionFromAPI) > VERSION) {
      if (!newVersion) { setNewVersion(true); }
    }
  };

  const isLoggedIn = !!credentials?.accessToken;

  useEffect(updateFromStorage, []);

  const apolloClientForAuth = setupApolloClient({
    uri: secrets.api.baseUri + '/auth',
    clearCredentials,
    checkVersion,
  });

  let apolloClient = null;

  const headers = isLoggedIn ? {
    ...credentials,
    'access-token': credentials.accessToken,
  } : {};

  if (isLoggedIn) {
    apolloClient = setupApolloClient({
      uri: secrets.api.baseUri + '/graphql',
      user: data?.currentUser,
      headers,
      clearCredentials,
      checkVersion,
    });
  }

  const updateUserId = id => {
    updateUserIdWithoutLog(id);

    if (apolloClient && id) {
      apolloClient.mutate({
        mutation: CREATE_LOGIN,
        variables: { userId: id }
      });
    }
  };

  const refetchData = () => {
    if (data) { setData(null) };
    apolloClient.query({ query: CURRENT_USER }).then(response => {
      setDataLoaded(true);
      setData(response.data);
      setLoginAttempted(true);
    });
  };

  if (!data && isLoggedIn) {
    refetchData();
  }

  const hasPolicy = ({ type, action }) => {
    if (!data || !data.currentUser) {
      return false;
    }

    return data.currentUser.policies.find(policy => (
      policy.model === type && policy.action === action
    ));
  };

  const value = {
    user: data?.currentUser,
    currentUser: data?.currentUser,
    role: data?.currentUser?.role,
    accounts: data?.accounts,
    feeTypes: (data?.feeTypes || []),
    paddleServices: data?.paddleServices,
    paymentTerms: data?.paymentTerms,
    lotPrefixes: data?.lotPrefixes,
    settings: data?.settings,
    apolloClient,
    apolloClientForAuth,
    isLoggedIn,
    credentials,
    headers,
    updateCredentials,
    clearCredentials,
    refetchData,
    rememberMe,
    updateRememberMe,
    loginAttempted,
    setLoginAttempted,
    dataLoaded,
    setDataLoaded,
    storageChecked,
    hasPolicy,
    showNewVersion: (newVersion && !ignoreVersion),
    setIgnoreVersion,
    availableForChat: data?.currentUser.availableForChat,
    cableMonitor: cable.connection.monitor,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
